<script setup lang="ts">
import { ref } from "vue";
import { mockFAQCategories } from "@/assets/ts/faqItems";

// 各質問の展開状態を管理
const expandedQuestions = ref<Record<string, boolean>>({});

// 質問の展開/折りたたみを切り替え
const toggleQuestion = (categoryIndex: number, questionIndex: number) => {
  const key = `${categoryIndex}-${questionIndex}`;
  expandedQuestions.value[key] = !expandedQuestions.value[key];
};

// 質問が展開されているかチェック
const isQuestionExpanded = (
  categoryIndex: number,
  questionIndex: number,
): boolean => {
  const key = `${categoryIndex}-${questionIndex}`;
  return expandedQuestions.value[key] || false;
};
</script>

<template>
  <div class="qa-container">
    <!-- 各カテゴリをループで表示 -->
    <div v-for="(categoryData, categoryIndex) in mockFAQCategories" :key="categoryIndex" class="category-section">
      <!-- カテゴリヘッダー -->
      <HeadingCorp :level="3" class="category-title">{{ categoryData.category }}</HeadingCorp>
      <!-- 質問と回答のリスト -->
      <div class="questions-list">
        <div v-for="(item, questionIndex) in categoryData.items" :key="questionIndex" class="qa-item">
          <!-- Q部分（クリック可能） -->
          <div class="question-header" @click="toggleQuestion(categoryIndex, questionIndex)">
            <div class="question-circle">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12" viewBox="0 0 10 12" fill="none">
                <path
                  d="M6.50374 10.6484C6.07406 10.7917 5.55973 10.8633 4.96077 10.8633C3.71077 10.8633 2.67779 10.49 1.86182 9.74349C0.872233 8.84505 0.377441 7.52561 0.377441 5.78516C0.377441 4.03168 0.885254 2.70573 1.90088 1.80729C2.72987 1.07378 3.76069 0.707031 4.99333 0.707031C6.23465 0.707031 7.27631 1.09549 8.11833 1.8724C9.09055 2.77083 9.57666 4.02734 9.57666 5.64193C9.57666 6.49696 9.47249 7.21311 9.26416 7.79036C9.09489 8.34158 8.84532 8.79948 8.51546 9.16406L9.62223 10.1992L8.57406 11.293L7.4152 10.1992C7.06364 10.4119 6.75982 10.5616 6.50374 10.6484ZM6.07406 8.91667L5.104 7.99219L6.13916 6.91146L7.10921 7.83594C7.26112 7.52344 7.36746 7.25 7.42822 7.01562C7.52371 6.66406 7.57145 6.25391 7.57145 5.78516C7.57145 4.70877 7.3501 3.8776 6.90739 3.29167C6.46902 2.70139 5.82666 2.40625 4.98031 2.40625C4.18604 2.40625 3.55235 2.68837 3.07926 3.2526C2.60617 3.81684 2.36963 4.66102 2.36963 5.78516C2.36963 7.10026 2.70817 8.0421 3.38525 8.61068C3.82362 8.9796 4.3488 9.16406 4.96077 9.16406C5.19081 9.16406 5.41216 9.13585 5.62484 9.07943C5.74202 9.04904 5.89176 8.99479 6.07406 8.91667Z"
                  fill="white" />
              </svg>
            </div>
            <div class="question-text">{{ item.question }}</div>
            <div class="arrow-icon">
              <Icons :name="isQuestionExpanded(categoryIndex, questionIndex) ? 'chevron-up' : 'chevron-down'" />
            </div>
          </div>

          <!-- A部分（展開時のみ表示） -->
          <Transition name="slide">
            <div v-if="isQuestionExpanded(categoryIndex, questionIndex)" class="answer-section">
              <Transition name="fade" appear>
                <div class="answer-content">
                  <div class="answer-circle">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M6.75114 7.8256H3.21598L2.55192 9.79826H0.455566L3.88005 0.201904H6.14567L9.54411 9.79826H7.36963L6.75114 7.8256ZM6.19124 6.17196L4.99333 2.39591L3.75635 6.17196H6.19124Z"
                        fill="white" />
                    </svg>
                  </div>
                  <div class="answer-text" v-html="item.answer"></div>
                </div>
              </Transition>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.category-section {
  margin: 2.5rem auto;
}

.category-title {
  margin-top: 2.5rem;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem; // 各質問間の間隔
}

.qa-item {
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #fff;
}

.question-header {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  cursor: pointer;
  background-color: #FFF;
  gap: 0.5rem;
  border-bottom: 1px solid #D3D3CB;

  &:hover {
    background: linear-gradient(90deg, #EFF7FF 0%, #F0FFFF 100%);
  }
}

.question-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #FFF;
  font-weight: 700;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: linear-gradient(270deg, #059696 0%, #2174C0 100%);

  svg {
    width: 0.57rem;
    height: 0.66rem;
  }
}

.question-text {
  flex: 1;
  color: #252522;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 150%;
}

.arrow-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #2174C0;
  transition: transform 0.2s ease;
}

.answer-section {
  background-color: #FAFAF7;
  overflow: hidden;
}

.answer-content {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
}

.answer-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #FFF;
  font-weight: 700;
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
  border-radius: 50%;
  background: linear-gradient(270deg, #727268 0%, #252522 100%);

  svg {
    width: 0.57rem;
    height: 0.66rem;
  }
}

.answer-text {
  flex: 1;
  color: #585851;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 150%;

  :deep(a) {
    color: #2174C0;
    text-decoration: underline;
  }
}

// スライドアニメーション
.slide-enter-active {
  transition: all 0.5s ease-out;
  overflow: hidden;
}

.slide-leave-active {
  transition: all 0.3s;
  overflow: hidden;
}

.slide-enter-from {
  max-height: 0;
  opacity: 0;
}

.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 200px;
  opacity: 1;
}

// フェードアニメーション
.fade-enter-active {
  transition: opacity 0.2s ease 0.1s;
}

.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
}

.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

// PC用のスタイル
@include mixin.media(pc) {
  .question-header {
    padding: 1rem;
  }

  .answer-content {
    padding: 1rem;
  }

  .slide-enter-to,
  .slide-leave-from {
    max-height: 250px;
  }

  .category-title {
    margin-top: 3rem;
  }
}
</style>